import { useEffect, useState } from "react";
import { Box, Button, Text } from "@salestrak/ui";

import { SalesTrakLogo } from "#app/components/brand/salestrak-logo";
import { AnalyticsContext, NavigationEvent, trackEvent } from "#app/lib/analytics";
import { usePersistedState } from "#app/lib/hooks/use-persisted-state";
import { useUserSetting } from "#app/lib/hooks/use-user-setting";
import { detectMobilePlatform, mobileAppUrl } from "#app/lib/mobile-app-urls";

const MOBILE_APP_NOTICE_KEY = "mobile-app-notice";

const MIRROR_KEY = "mobile-app-notice-mirror";
const MIRROR_DEFAULTS: { dismissedAt: number | null } = { dismissedAt: null };

export const MOBILE_APP_NOTICE_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

export function parseDismissedAt(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function isNoticeSnoozed(dismissedAt: number | null, now: number): boolean {
  return dismissedAt !== null && now - dismissedAt < MOBILE_APP_NOTICE_SNOOZE_MS;
}

export function MobileAppNotice() {
  const [platform] = useState(() =>
    typeof navigator === "undefined"
      ? null
      : detectMobilePlatform(navigator.userAgent, navigator.maxTouchPoints),
  );
  const url = platform ? mobileAppUrl(platform) : "";
  const eligible = url !== "";
  const [now] = useState(() => Date.now());
  const { value, setValue, initialLoading, loaded } = useUserSetting(
    MOBILE_APP_NOTICE_KEY,
    parseDismissedAt,
    { skip: !eligible },
  );
  // Until the server answers successfully, this browser's copy decides, so the page never flashes first.
  const { state: mirror, update: updateMirror } = usePersistedState(
    MIRROR_KEY,
    MIRROR_DEFAULTS,
  );
  const dismissedAt = loaded ? value : mirror.dismissedAt;

  useEffect(() => {
    if (loaded && mirror.dismissedAt !== value) {
      updateMirror({ dismissedAt: value });
    }
  }, [loaded, mirror.dismissedAt, updateMirror, value]);

  const visible = eligible && !isNoticeSnoozed(dismissedAt, now);
  const settled = !initialLoading;
  useEffect(() => {
    if (!visible || !settled) return;
    trackEvent({
      context: AnalyticsContext.Navigation,
      event: NavigationEvent.MobileAppNoticeShown,
      data: { platform },
    });
  }, [visible, settled, platform]);

  if (!visible) return null;

  const download = () => {
    trackEvent({
      context: AnalyticsContext.Navigation,
      event: NavigationEvent.MobileAppDownload,
      data: { platform },
    });
    window.location.assign(url);
  };

  const dismiss = () => {
    trackEvent({
      context: AnalyticsContext.Navigation,
      event: NavigationEvent.MobileAppNoticeDismiss,
      data: { platform },
    });
    const at = Date.now();
    updateMirror({ dismissedAt: at });
    setValue(at);
  };

  return (
    <Box
      zIndex={50}
      position="fixed"
      inset={0}
      direction="column"
      align="center"
      background="background"
      padding="xl"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mobile-app-notice-title"
    >
      <Box fill="flex" direction="column" align="center" justify="center" maxWidth={360}>
        <SalesTrakLogo size="hero" showText={false} />
        <Box paddingTop="xl" paddingBottom="sm">
          <Text id="mobile-app-notice-title" as="h1" size="2xl" weight="semibold" align="center">
            Get the SalesTrak app
          </Text>
        </Box>
        <Text variant="muted" align="center">
          For the best experience, use the mobile app.
        </Text>
      </Box>
      <Box direction="column" fill="width" gap="md">
        <Button size="xl" shape="square" fullWidth onClick={download}>
          Get the mobile app
        </Button>
        <Button size="xl" shape="square" variant="outline" fullWidth onClick={dismiss}>
          Continue to website
        </Button>
      </Box>
    </Box>
  );
}
